import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC, useMemo } from "react";
import CopilotButton from "../../../../components/CopilotButton";
import { ExpandableDetails } from "../../../../components/ExpandableDetails";
import { PercentageFilterChip } from "../../../../components/filters/Percentage/PercentageFilterChip";
import { SpendCategoryFilterChip } from "../../../../components/filters/SpendCategory/SpendCategoryFilterChip";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import {
  CopilotWorkflow,
  useCopilotStore,
} from "../../../../store/copilot/CopilotStore";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { HgSpendCategory } from "../../../../types/taxonomy";
import { Workspace } from "../../../../types/workspace";
import { totalExternalItSpendCategory } from "../../../../utils/consts";

export const Tam: FC = () => {
  const workspace = useWorkspaceStore((state) => state.draft);
  const setWorkspace = useWorkspaceStore((state) => state.setDraft);
  const { spendCategories } = useTaxonomyContext();
  const highlightedElement = useCopilotStore(
    (state) => state.highlightedElement
  );

  const selectedSpendCategoriesIds = useMemo(
    () => workspace?.configuration.tam.spendCategoryIds || [],
    [workspace?.configuration.tam.spendCategoryIds]
  );

  const selectedSpendCategoriesWeights = useMemo(
    () => workspace?.configuration.tam.spendCategoryWeights || [],
    [workspace?.configuration.tam.spendCategoryWeights]
  );

  const selectedSpendCategories = useMemo(
    () =>
      spendCategories.filter((spendCategory) =>
        selectedSpendCategoriesIds.includes(spendCategory.id)
      ),
    [selectedSpendCategoriesIds, spendCategories]
  );

  const handleOnSelectedItems = (selectedItems: string[]) => {
    // prevent user from having no spendCategories applied, auto apply externalIt in that case
    let filteredSelectedItems: string[] = [];

    filteredSelectedItems =
      selectedItems.length === 0
        ? [totalExternalItSpendCategory]
        : selectedItems.filter((itemId) => {
            const currentItem = spendCategories.find(
              (category) => category.id === itemId
            );

            if (!currentItem || !currentItem.parentId) {
              return true;
            }

            return !selectedItems.includes(currentItem.parentId);
          });

    setWorkspace(
      (draft) =>
        ({
          ...draft,
          configuration: {
            ...draft?.configuration,
            tam: {
              spendCategoryIds: filteredSelectedItems,
              spendCategoryWeights: filteredSelectedItems.map((id) => ({
                id,
                weight: 100,
              })),
            },
          },
        }) as Workspace
    );
  };

  const handleOnWeightChange = (
    spendCategoryId: HgSpendCategory["id"],
    value: number
  ) => {
    setWorkspace(
      (draft) =>
        ({
          ...draft,
          configuration: {
            ...draft?.configuration,
            tam: {
              ...draft?.configuration.tam,
              spendCategoryWeights:
                draft?.configuration.tam.spendCategoryWeights.map(
                  (spendCategoryWeight) =>
                    spendCategoryWeight.id === spendCategoryId
                      ? { id: spendCategoryId, weight: value }
                      : spendCategoryWeight
                ),
            },
          },
        }) as Workspace
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
      <Stack
        direction="column"
        gap={1}
        sx={{ position: "relative" }}
        alignItems="start"
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 1,
          }}
        >
          <CopilotButton
            id="copilot-tam-button"
            workflow={CopilotWorkflow.TAM}
          />
        </Box>
        <Stack
          direction="row"
          alignItems="start"
          justifyContent="space-between"
          spacing={1}
          sx={{ pr: "40px" }}
        >
          <Typography>
            The total global budget available for my product(s) or service(s)
            comes from {selectedSpendCategories.length > 1 ? "these" : "this"}
          </Typography>
        </Stack>

        <SpendCategoryFilterChip
          id="spend-category"
          selectedItems={selectedSpendCategoriesIds}
          onSelectedItems={handleOnSelectedItems}
          highlighted={highlightedElement === "spend_category_chip"}
        />

        <Box mt={2}>
          <ExpandableDetails id="spend-category-details">
            <Stack
              flexDirection="row"
              gap={1}
              flexWrap="wrap"
              alignItems="center"
            >
              <Typography variant="body1"> I estimate that</Typography>

              {selectedSpendCategories.map((spendCategory, index) => (
                <Stack
                  key={spendCategory.id}
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <PercentageFilterChip
                    id={`spend-category-weight-${spendCategory.id}`}
                    value={
                      selectedSpendCategoriesWeights.find(
                        ({ id }) => id === spendCategory.id
                      )?.weight
                    }
                    onChange={(value) =>
                      handleOnWeightChange(spendCategory.id, value)
                    }
                  />

                  <Chip
                    id={`spend-category-name-${spendCategory.id}`}
                    label={spendCategory.name}
                  />

                  {index < selectedSpendCategories.length - 1 &&
                    (index === selectedSpendCategories.length - 2 ? (
                      <Typography variant="body1">and</Typography>
                    ) : (
                      <Typography variant="body1">,</Typography>
                    ))}
                </Stack>
              ))}

              <Typography variant="body1">
                {selectedSpendCategories.length > 1 ? "are" : "is"} relevant to
                our solution, based on the portion that aligns with our specific
                market and product offerings.
              </Typography>
            </Stack>
          </ExpandableDetails>
        </Box>
      </Stack>
    </Paper>
  );
};
